import { Component, Injector, inject, viewChild } from '@angular/core';
import { AppComponentBase } from '@shared/common/app-component-base';
import {
    AuditLogServiceProxy,
    EntityChangeListDto,
    EntityPropertyChangeDto,
} from '@shared/service-proxies/service-proxies';
import { DateTime } from 'luxon';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { DateTimeService } from '../timing/date-time.service';
import { AppBsModalDirective } from '@shared/common/appBsModal/app-bs-modal.directive';
import { EzJsonComparisonComponent } from '@app/shared/ez-json-comparison/ez-json-comparison.component';
import { LocalizePipe } from '@shared/common/pipes/localize.pipe';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'entityChangeDetailModal',
    templateUrl: './entity-change-detail-modal.component.html',
    imports: [AppBsModalDirective, LocalizePipe, CommonModule, EzJsonComparisonComponent],
})
export class EntityChangeDetailModalComponent extends AppComponentBase {
    private _auditLogService = inject(AuditLogServiceProxy);
    private _dateTimeService = inject(DateTimeService);

    readonly modal = viewChild<ModalDirective>('entityChangeDetailModal');

    active = false;
    entityPropertyChanges: EntityPropertyChangeDto[];
    entityChange: EntityChangeListDto;

    constructor(...args: unknown[]);

    constructor() {
        const injector = inject(Injector);

        super(injector);
    }

    getPropertyChangeValue(propertyChangeValue, propertyTypeFullName) {
        if (!propertyChangeValue) {
            return propertyChangeValue;
        }
        propertyChangeValue = propertyChangeValue.replace(/^['"]+/g, '').replace(/['"]+$/g, '');
        if (this.isDate(propertyChangeValue, propertyTypeFullName)) {
            return this._dateTimeService.formatDate(
                this._dateTimeService.fromISODateString(propertyChangeValue),
                'yyyy-LL-dd HH:mm:ss'
            );
        }

        if (propertyChangeValue === 'null') {
            return '';
        }

        return propertyChangeValue;
    }

    isDate(date, propertyTypeFullName): boolean {
        return propertyTypeFullName.includes('DateTime') && !isNaN(Date.parse(date).valueOf());
    }

    isJsonValue(value: string): boolean {
        if (!value || typeof value !== 'string') {
            return false;
        }

        // Trim the value
        const trimmedValue = value.trim();

        // Must start with { or [ to be considered JSON
        if (!trimmedValue.startsWith('{') && !trimmedValue.startsWith('[')) {
            return false;
        }

        try {
            const parsed = JSON.parse(trimmedValue);
            // Must be an object or array (not primitive values)
            const isValidJson = typeof parsed === 'object' && parsed !== null;
            console.log(`[EntityHistory] JSON Detection - Value: "${trimmedValue.substring(0, 50)}..." -> IsJson: ${isValidJson}`);
            return isValidJson;
        } catch (error) {
            console.log(`[EntityHistory] JSON Detection - Value: "${trimmedValue.substring(0, 50)}..." -> IsJson: false (Parse error)`);
            return false;
        }
    }

    isJsonProperty(propertyChange: EntityPropertyChangeDto): boolean {
        // Check if either original or new value is JSON
        const originalIsJson = this.isJsonValue(propertyChange.originalValue);
        const newIsJson = this.isJsonValue(propertyChange.newValue);

        const result = originalIsJson || newIsJson;
        console.log(`[EntityHistory] Property "${propertyChange.propertyName}" -> Original JSON: ${originalIsJson}, New JSON: ${newIsJson}, Result: ${result}`);

        return result;
    }

    show(record: EntityChangeListDto): void {
        const self = this;
        self.active = true;
        self.entityChange = record;

        this._auditLogService.getEntityPropertyChanges(record.id).subscribe((result) => {
            self.entityPropertyChanges = result;
        });

        self.modal().show();
    }

    close(): void {
        this.active = false;
        this.modal().hide();
    }
}
